<template>
    <div class="students">
        <AddStudentsSidePanel
            v-if="showAddStudentSidePanel"
            @close="closeAddStudentsSidePanel"
        />
        <EditStudentSidePanel
            v-if="showEditStudentSidePanel && editStudentId"
            :org-student-id="editStudentId"
            @close="showEditStudentSidePanel = false"
        />
        <ResendInviteSidePanel
            :org-student-ids="selectedStudentsTypes.Pending?.map(s => s.id) || []"
            v-if="showResendInviteEmailSidePanel"
            @close="showResendInviteEmailSidePanel = false"
        />
        <AddToGroupSidePanel
            v-if="showAddToGroupSidePanel"
            :org-student-ids="studentIds"
            :org-exam-ids="getSelectedStudentOrgExamIds()"
            @close="() => showAddToGroupSidePanel = false"
        />
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="showPurchaseSeatsModal = false"
        />
        <Portal to="modal">
            <ModalContainer
                v-if="showArchiveStudentModal"
                :show-close-button="true"
                :remove-head-border-bottom="true"
                @close="showArchiveStudentModal = false">
                <template #head>
                    <div
                        class="students__cancel-invite-head"
                    >
                        Archive Student?
                    </div>
                </template>
                <div
                    class="students__cancel-modal"
                >
                    <span
                        class="students__cancel-text"
                    >
                        All of their active seats will be archived
                        and they will be removed from all groups.
                        They can still access their account and study progress.
                    </span>
                    <div
                        class="students__cancel-buttons"
                    >
                        <PocketButton
                            class="students__nevermind-btn"
                            type="tertiary"
                            @click="showArchiveStudentModal = false"
                        >
                            Never Mind
                        </PocketButton>
                        <PocketButton
                            class="students__cancel-btn"
                            type="primary-red"
                            @click="submitArchiveSeats"
                        >
                            Archive {{ selectedStudentsTypes.Active?.length }} Student(s)
                        </PocketButton>
                    </div>
                </div>
            </ModalContainer>
            <ModalContainer
                v-if="showCancelInviteModal"
                :show-close-button="true"
                :remove-head-border-bottom="true"
                @close="showCancelInviteModal = false">
                <template #head>
                    <div
                        class="students__cancel-invite-head"
                    >
                        Cancel Invites?
                    </div>
                </template>
                <div
                    class="students__cancel-modal"
                >
                    <span
                        class="students__cancel-text"
                    >
                        Student invites will be cancelled and their license code will no longer work.
                    </span>
                    <div
                        class="students__cancel-buttons"
                    >
                        <PocketButton
                            class="students__nevermind-btn"
                            type="tertiary"
                            @click="showCancelInviteModal = false"
                        >
                            Never Mind
                        </PocketButton>
                        <PocketButton
                            class="students__cancel-btn"
                            type="primary-red"
                            @click="submitCancelInvites"
                        >
                            Cancel {{ selectedStudentsTypes.Pending?.length }} Invite(s)
                        </PocketButton>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
        <NoSeatsBanner
            class="students__no-available-seats-banner"
            v-if="isExamMissingLicenses && basicStudents.length"
            :num-students-missing-licenses="basicStudents.length"
        />
        <div class="students__tabs">
            <div
                class="students__tab"
                :class="{ active: activeTab === 'student' }"
                @click="activeTab = 'student'"
            >
                Students ({{ studentCounts.student.size }})
            </div>
            <div
                class="students__tab"
                :class="{ active: activeTab === 'archived' }"
                @click="activeTab = 'archived'"
            >
                Archived ({{ studentCounts.archived.size }})
            </div>
            <div v-if="isLoading">
                <Icon type="loading" />
            </div>
            <div v-else-if="isProcessingStudents" class="students__processing-indicator">
                <Icon type="loading" />
                <span>Processing students...</span>
            </div>
        </div>
        <div v-if="!isLoading" class="students__table-head">
            <Search
                v-model="searchString"
                class="students__table-search"
                placeholder="Search"
                type="white"
            />
            <div
                class="students__filter"
            >
                <PocketButton
                    type="icon"
                    class="students__filter-btn"
                    :icon="(filterPrep.length || filterStatuses.length) ? 'filterActive' : 'filter'"
                    :aria-label="`${
                        (filterPrep.length || filterStatuses.length) ? 'Filters selected, ' : ''
                    }Show filters`"
                    @click="showFilterDropdown = !showFilterDropdown"
                    @keypress.enter="showFilterDropdown = !showFilterDropdown"
                    @mousedown.prevent
                />
                <MultiFilterDropdown
                    v-show="showFilterDropdown"
                    :settings-students="dropdownActiveTabStudents"
                    :active-tab="activeTab"
                    :prep-filters-to-remove="prepFiltersToRemove || undefined"
                    :status-filters-to-remove="statusFiltersToRemove || undefined"
                    class="students__filter-dropdown"
                    aria-live="polite"
                    @prepFilterChange="updatePrepFilter"
                    @statusesFilterChange="updateStatusesFilter"
                    @close="showFilterDropdown = false"
                />
                <div
                    class="students__filter-text"
                    @click="showFilterDropdown = !showFilterDropdown"
                    @keypress.enter="showFilterDropdown = !showFilterDropdown"
                    @mousedown.prevent
                >
                    Filter Students
                </div>
            </div>
            <TablePagination
                class="students__table-pagination"
                :per-page="perPage"
                unit="students"
                :current-page="currentPage"
                :total="filteredActiveTabStudents.length"
                @changePage="changePage($event)"
            />
        </div>
        <div
            v-if="!isLoading && filterPrep.length || filterStatuses.length"
            class="students__filter-chips">
            <span class="students__filter-chips-label">Filters:</span>
            <Pill
                v-for="filter in filterPrep"
                :key="filter"
                :show-close="true"
                class="students__filter-chip"
                @click="removePrepFilters([ filter ])"
            >
                {{ filter }}
            </Pill>
            <Pill
                v-for="filter in filterStatuses"
                :key="filter"
                :show-close="true"
                class="students__filter-chip"
                @click="removeStatusFilters([ filter ])"
            >
                {{ filter }}
            </Pill>
        </div>
        <div v-if="!isLoading" class="students__bulk-actions">
            <Checkbox
                :modelValue="mappedModelValue"
                class="students__bulk-actions-checkbox"
                @click="selectAllStudents"
            />
            <div
                class="students__bulk-actions-num-checked"
            >
                {{ studentIds.length }} of {{ filteredActiveTabStudents.length }} selected
            </div>
            <div
                class="students__add-to-group"
            >
                <Tooltip
                    v-if="showAddToGroupTooltip && addToGroupDisabled"
                    class="students__add-to-group-tooltip"
                >
                    {{
                        studentIds.length
                            ? 'Groups can only contain students with the same exam.'
                            : 'No students are selected.'
                    }}
                </Tooltip>
                <PocketButton
                    v-if="activeTab === 'student'"
                    class="students__add-to-group-btn"
                    type="tertiary"
                    @mouseover="showAddToGroupTooltip = true"
                    @mouseout="showAddToGroupTooltip = false"
                    :disabled="addToGroupDisabled"
                    @click="showAddToGroupSidePanel = true"
                >
                    Add to Group
                </PocketButton>
            </div>
            <div
                class="students__resend-invite"
                @mouseover="showResendInviteTooltip = true"
                @mouseout="showResendInviteTooltip = false"
            >
                <Tooltip
                    v-if="showResendInviteTooltip && cancelAndResendInviteDisabled"
                    class="students__resend-invite-tooltip"
                >
                    No pending students are selected.
                </Tooltip>
                <PocketButton
                    v-if="activeTab === 'student'"
                    class="students__resend-invite-btn"
                    type="tertiary"
                    :disabled="cancelAndResendInviteDisabled"
                    @click="showResendInviteEmailSidePanel = true"
                >
                    Resend Invite
                </PocketButton>
            </div>
            <div
                class="students__cancel-invite"
                @mouseover="showCancelInviteTooltip = true"
                @mouseout="showCancelInviteTooltip = false"
            >
                <Tooltip
                    v-if="showCancelInviteTooltip && cancelAndResendInviteDisabled"
                    class="students__resend-invite-tooltip"
                >
                    No pending students are selected.
                </Tooltip>
                <PocketButton
                    v-if="activeTab === 'student'"
                    class="students__cancel-invite-btn"
                    type="tertiary-red"
                    :disabled="cancelAndResendInviteDisabled"
                    @click="showCancelInviteModal = true"
                >
                    Cancel Invite
                </PocketButton>
            </div>
            <div
                class="students__archive-student"
                @mouseover="showArchiveStudentTooltip = true"
                @mouseout="showArchiveStudentTooltip = false"
            >
                <Tooltip
                    v-if="showArchiveStudentTooltip && archiveStudentDisabled"
                    class="students__archive-student-tooltip"
                >
                    No active students are selected.
                </Tooltip>
                <PocketButton
                    v-if="activeTab === 'student'"
                    class="students__archive-student-btn"
                    type="tertiary-red"
                    :disabled="archiveStudentDisabled"
                    @click="showArchiveStudentModal = true"
                >
                    Archive Student
                </PocketButton>
            </div>
            <PocketButton
                v-if="activeTab === 'archived'"
                class="students__unarchive"
                type="tertiary"
                :disabled="!studentIds.length"
                @click="submitUnarchiveSeats"
            >
                Unarchive
            </PocketButton>
        </div>
        <Table
            v-if="!isLoading"
            :key="`${activeTab}_students`"
            :rows="pagedActiveTabStudents"
            :columns="activeTabColumns"
            :default-sort="sortSettings"
            :sort-toggle-order="[ 1, -1 ]"
            :show-header="false"
            :table-row-styles="{
                paddingRight: '0',
                height: '45px',
            }"
            :table-column-labels-styles="{
                paddingRight: '0'
            }"
            :table-cell-styles="{
                position: 'relative',
            }"
            plural-item-name="Students"
            tooltip-text="Edit Student"
            class="students__table"
            @sort="updateSortSettings($event)"
            @rowEnter="rowEnter"
            @rowLeave="rowLeave"
            @rowClicked="rowClicked({id: $event.id as string})"
        >
            <template #tableCellValue="{ row, column }">
                <div
                    v-if="column.propName === 'studentNameOrEmail'"
                    class="students__checkbox-name"
                >
                    <Checkbox :modelValue="studentIdSet.has(row.id)" class="students__checkbox" />
                    <div class="students__name">
                        <OverflowTooltip>
                            {{ row.studentNameOrEmail }}
                        </OverflowTooltip>
                    </div>
                </div>
                <div
                    v-if="column.propName === 'instructors'"
                    class="students__instructors"
                >
                    {{ row.instructors }}
                </div>
                <template v-if="column.propName === 'status'">
                    <div
                        class="students__statuses"
                    >
                        <Tag
                            v-if="row.isArchived && activeTab === 'archived'"
                            :isArchivedTag="true"
                            size="small"
                        >
                            <template #tagMessage>
                                Archived
                            </template>
                        </Tag>
                        <div
                            v-else
                            class="students__statuses"
                        >
                            <Tag
                                v-if="row.isActive"
                                :isActiveTag="true"
                                size="small"
                                class="students__status-active"
                            >
                                <template #tagMessage>
                                    Active
                                </template>
                            </Tag>
                            <Tag
                                v-if="row.isPending"
                                :isPendingTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Pending
                                </template>
                            </Tag>
                        </div>
                    </div>
                </template>
                <template v-else-if="column.propName === 'edit'">
                    <Tooltip
                        v-if="cellHovered
                            && (cellHovered.propName === column.propName && cellHovered.id === row.id)"
                        class="students__edit-tooltip"
                    >
                        Edit {{ row.isActive || row.isArchived ? 'Student' : 'Invite' }}
                    </Tooltip>
                    <Icon
                        v-show="rowHoverId === row.id && (!('hideRowEdit' in row) || !row.hideRowEdit)"
                        class="students__edit"
                        type="edit"
                        @mouseover="showCellTooltip(column.propName, row.id)"
                        @mouseout="hideCellTooltip"
                        @click.stop="editStudent(row.id)"
                    />
                </template>
            </template>
            <template #tableRows>
                <EmptyState
                    v-if="!Object.values(currentOrgStudents).length || isProcessingStudents"
                    class="students__empty-state"
                    :title="emptyStateTitle"
                    :message="emptyStateMessage"
                >
                    <template #imageRow>
                        <div />
                    </template>
                </EmptyState>
                <PocketButton
                    v-if="!currentOrgLicenses.length && showPurchaseSeatsButton"
                    class="students__add-student-empty-btn"
                    @click="showPurchaseSeatsSidePanel"
                >
                    Purchase Seats
                </PocketButton>
                <PocketButton
                    v-else-if="!Object.values(currentOrgStudents).length && activeTab !== 'archived'"
                    class="students__add-student-empty-btn"
                    @click="showAddStudentSidePanel = true"
                >
                    Add Student
                </PocketButton>
            </template>
        </Table>
        <PocketButton
            v-if="!isLoading && activeTab !== 'archived'"
            type="secondary"
            @click="showAddStudentSidePanel = true">
            + Add New Student
        </PocketButton>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit, { type ITableSortSettings, type ITableColumn, type TTableRow } from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import AddStudentsSidePanel from '@/components/SidePanels/AddStudents.vue'
import EditStudentSidePanel from '@/components/SidePanels/EditStudent.vue'
import { formatDate } from '@/utils'
import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { licenseModule } from '@/store/license/module'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { toastModule } from '@/store/toast/module'
import { analyticsModule } from '@/store/analytics/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import ResendInviteSidePanel from '@/components/SidePanels/ResendInvite.vue'
import AddToGroupSidePanel from '@/components/SidePanels/AddToGroup.vue'
import MultiFilterDropdown from '@/components/Filters/MultiFilterDropdown.vue'
import NoSeatsBanner from '@/components/NoSeatsBanner.vue'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'

const {
    ModalContainer,
    Table,
    Button: PocketButton,
    Icon,
    Tooltip,
    Tag,
    Search,
    TablePagination,
    Checkbox,
    OverflowTooltip,
    Pill,
    EmptyState,
} = UIKit

type TMappedStudent = {
    id: string
    studentNameOrEmail: string
    prepDisplayString: string
    prep: string[]
    studentExamGuids: string[]
    instructors: string
    startDate: string
    styles: { [key: string]: string }
    isPending: boolean
    isActive: boolean // A student can be considered both pending and active!
    isArchived: boolean
}

type TStudents = {
    [key: string]: TMappedStudent[]
}

const props = withDefaults(defineProps<{
    forceShowStudentsSidePanel?: boolean
}>(), {
    forceShowStudentsSidePanel: false,
})

const route = useRoute()
const router = useRouter()

const activeTab = ref<'student' | 'archived'>('student')
const searchString = ref('')
const showAddStudentSidePanel = ref(false)
const showEditStudentSidePanel = ref(false)
const editStudentId = ref<string | null>(null)
const rowHoverId = ref<string | number | null>(null)
const studentIdSet = ref(new Set<string>())
const allStudentsSelected = ref(false)
const cellHovered = ref<null | { propName: string; id: string }>(null)
const currentPage = ref(0)
const perPage = ref(20)
const isLoading = ref(true)
const isProcessingStudents = ref(false)
const mappedActiveStudentsCache = ref<TMappedStudent[]>([])
const latestProcessingStartTime = ref(0)

// Bulk Actions
const showResendInviteEmailSidePanel = ref(false)
const showCancelInviteModal = ref(false)
const showArchiveStudentModal = ref(false)
const showAddToGroupSidePanel = ref(false)

// Bulk actions tooltips
const showAddToGroupTooltip = ref(false)
const showResendInviteTooltip = ref(false)
const showCancelInviteTooltip = ref(false)
const showArchiveStudentTooltip = ref(false)

// Side Panels
const showPurchaseSeatsModal = ref(false)

// Filtering
const showFilterDropdown = ref(false)
const filterPrep = ref<string[]>([])
const filterStatuses = ref<string[]>([])
const checkboxPrep = ref<string[]>([])
const checkboxStatuses = ref<string[]>([])
const prepFiltersToRemove = ref<string[] | null>(null)
const statusFiltersToRemove = ref<string[] | null>(null)

const studentColumns: ITableColumn[] = [{
    name: 'Student',
    propName: 'studentNameOrEmail',
    styles: {
        width: '255px',
    },
}, {
    name: 'Prep',
    propName: 'prepDisplayString',
    styles: {
        width: '142px',
    },
}, {
    name: 'Instructor(s)',
    propName: 'instructors',
    isSortDisabled: true,
    styles: {
        width: '100px',
        overflow: 'visible',
    },
}, {
    name: 'Start Date',
    propName: 'startDate',
    styles: {
        width: '89px',
    },
}, {
    name: 'Status',
    propName: 'status',
    isSortDisabled: true,
    styles: {
        width: '103px',
    },
}, {
    propName: 'edit',
    isLabelHidden: true,
    styles: {
        width: '24px',
        margin: '0 0 0 0',
    },
}]

const archivedStudentColumns = computed((): ITableColumn[] => {
    return [{
        name: 'Student',
        propName: 'studentNameOrEmail',
        styles: {
            width: '220px',
        },
    }, {
        name: 'Prep',
        propName: 'prepDisplayString',
        styles: {
            width: '250px',
        },
    }, {
        name: 'Start Date',
        propName: 'startDate',
        styles: {
            width: '71px',
        },
    }, {
        name: 'Status',
        propName: 'status',
        isSortDisabled: true,
        styles: {
            width: '95px',
        },
    }, {
        propName: 'edit',
        isLabelHidden: true,
        styles: {
            width: '24px',
            margin: '0 0 0 0',
        },
    }]
})

const sortSettings = ref<ITableSortSettings>({ column: archivedStudentColumns.value[0], direction: 1 })

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const basicStudents = computed(() => {
    return groupModule.getters.getBasicStudents()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const guidOrgExamLib = computed(() => {
    return examModule.getters.getGuidOrgExamLib()
})

const studentIds = computed(() => {
    return Array.from(studentIdSet.value)
})

const examGuidLicenseLib = computed(() => {
    return licenseModule.getters.getExamGuidAvailableLicenseLib()
})

const isExamMissingLicenses = computed(() => {
    const currentExamGuid = examModule.getters.getCurrentExamMetadata()?.examGuid
    if (!currentExamGuid) {
        return false
    }
    return !examGuidLicenseLib.value[currentExamGuid]
})

const studentCounts = computed(() => {
    return currentOrgLicenses.value.reduce((acc, license) => {
        if (!currentOrgInstructor.value?.isAdmin
            && (!license.orgStudent?.objectId
                || !yourStudentIds.value.includes(license.orgStudent?.objectId))
        ) {
            return acc
        }
        // Student is pending/active OR basic
        const studentId = license.orgStudent?.objectId
        const isBasicStudent = basicStudents.value.some(id => license.orgStudent?.objectId === id)

        if ((license.status === 'pending' || license.status === 'consumed')
            || isBasicStudent && !acc.student.has(studentId)) {
            if (!studentId) {
                return acc
            }
            acc.student.add(studentId)
        }

        if (license.status === 'archived') {
            acc.archived.add(license.user?.objectId)
        }

        return acc
    }, { student: new Set(), archived: new Set() })
})

// Returns one of 'all', 'none', 'intermediate'
const studentsSelected = computed(() => {
    // Are all students selected? If yes, we return true (but we want to return false if
    // they aren't and null if we are intermediate state)
    return (studentIds.value.length && filteredActiveTabStudents.value.length
        && studentIds.value.length === filteredActiveTabStudents.value.length)
        ? 'all'
        : studentIds.value.length === 0 && !allStudentsSelected.value ? 'none' : 'intermediate'
})

const mappedModelValue = computed(() => {
    return studentsSelected.value === 'all'
        ? true
        : studentsSelected.value === 'none' ? false : null
})

const areAllOrgExamsFree = computed(() => {
    return examModule.getters.getAreAllOrgExamsFree()
})

const showPurchaseSeatsButton = computed(() => {
    // Only show if the current organization is newer than May 1, 2024
    return !areAllOrgExamsFree.value
        && organizationModule.getters.getHasSelfServicePaymentAccess()
})

const selectedStudentsTypes = computed(() => {
    const selectedStudentIdsVal = studentIds.value
    const pendingStudents = selectedStudentIdsVal.reduce<TStudents>((acc, sid) => {
        const matchingStudent = mappedStudents.value.find(ms => ms.id === sid)
        if (matchingStudent?.isActive) {
            acc.Active?.push(matchingStudent)
        }

        if (matchingStudent?.isPending) {
            acc.Pending?.push(matchingStudent)
        }
        return acc
    }, { Active: [], Pending: [] })

    return pendingStudents
})

const addToGroupDisabled = computed(() => {
    return selectedStudentsPrep.value.length === 0
        || selectedStudentsPrep.value.length > 1
        || selectedStudentsPrep.value.some(ssp => ssp.includes('Exams')) // More than 1 exam
})

const cancelAndResendInviteDisabled = computed(() => {
    // No pending students selected
    return selectedStudentsTypes.value.Pending?.length === 0
})

const archiveStudentDisabled = computed(() => {
    // No active students selected
    return selectedStudentsTypes.value.Active?.length === 0
})

// Only should be true if the selected students all have the same prep (and only one)
const selectedStudentsPrep = computed(() => {
    const selectedStudents = [ ...(selectedStudentsTypes.value.Active || []),
        ...(selectedStudentsTypes.value.Pending || []) ]
    return selectedStudents.reduce<string[]>((acc, student) => {
        const studentPrep = student.prep
        if (!acc.some(a => studentPrep.includes(a))) {
            acc.push(...studentPrep) // There's a chance that this will contain a string with __ Exams
        }
        return acc
    }, [])
})

const mappedBasicStudents = computed(() => {
    // Map any and all basic students to the same structure as Active students
    return basicStudents.value.reduce((acc, basicStudentId) => {
        // Extract student name
        const studentUserId = currentOrgStudents.value[basicStudentId]?.user?.objectId
        const studentUser = studentUserId ? currentOrgStudentUsers.value[studentUserId] : undefined
        const student = `${studentUser?.firstName} ${studentUser?.lastName}`

        // What exam(s) is the student studying?
        const prep: string[] = []
        const prepDisplayString = ''
        const studentExamGuids: string[] = []

        // Get instructor(s) for student
        const instructors = getInstructorNames(basicStudentId)

        acc.push({
            id: basicStudentId,
            styles: {},
            studentNameOrEmail: student,
            prepDisplayString,
            prep,
            studentExamGuids,
            instructors: instructors.length > 1
                ? `${instructors.length} Instructors`
                : instructors[0] || '--',
            startDate: '',
            // Will always be active and not pending or archived
            isPending: false,
            isActive: true,
            isArchived: false,
        })
        return acc
    }, [] as TMappedStudent[])
})

const mappedActiveStudents = computed(() => {
    // Return cached results - actual processing happens in processStudentsInChunks()
    return mappedActiveStudentsCache.value
})

const mappedStudents = computed(() => {
    // Ensure unique students
    const uniqueIdSet = new Set<string>()
    const uniqueStudents: TMappedStudent[] = []
    mappedActiveStudents.value.forEach(s => {
        if (!uniqueIdSet.has(s.id)) {
            uniqueStudents.push(s)
            uniqueIdSet.add(s.id)
        }
    })
    mappedBasicStudents.value.forEach(s => {
        if (!uniqueIdSet.has(s.id)) {
            uniqueStudents.push(s)
            uniqueIdSet.add(s.id)
        }
    })
    return uniqueStudents
})

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const yourCurrentOrgGroups = computed(() => {
    return yourOrgExamIds.value.flatMap(orgExamId => groupModule.getters.searchCurrentOrgGroups({
        examId: orgExamId,
    }))
})

const yourStudentIds = computed(() => {
    const ids = yourCurrentOrgGroups.value.reduce((acc, g) => {
        const groupStudentIds = g.orgStudents.map(s => s.objectId)
        return acc.concat(groupStudentIds)
    }, [] as string[])

    return Array.from(new Set(ids))
})

const activeTabStudents = computed(() => {
    return mappedStudents.value.filter(s => {
        return s.studentNameOrEmail.toLowerCase().includes(searchString.value.toLowerCase())
            && ((activeTab.value === 'archived' && s.isArchived)
                || (activeTab.value === 'student' && (s.isPending || s.isActive)))
    })
})

const dropdownActiveTabStudents = computed(() => {
    return activeTabStudents.value.map(ats => {
        return {
            studentId: ats.id,
            prep: ats.prep,
            status: ats.isActive && !ats.isPending
                ? 'Active'
                : !ats.isActive && ats.isPending
                    ? 'Pending'
                    : ats.isActive && ats.isPending
                        ? 'Mixed'
                        : ats.isArchived
                            ? 'Archived'
                            : 'Unknown',
        }
    })
})

const filteredActiveTabStudents = computed(() => {
    return activeTabStudents.value.filter(ats => {
        // Filtering on prep
        const checkPrepFilter = filterPrep.value.length === 0
            || filterPrep.value.length && filterPrep.value.some(fp => ats.prep.includes(fp))
        // Filtering on status
        const studentStatus = ats.isActive && !ats.isPending
            ? 'Active'
            : !ats.isActive && ats.isPending
                ? 'Pending'
                : ats.isActive && ats.isPending
                    ? 'Mixed'
                    : ats.isArchived
                        ? 'Archived'
                        : 'Unknown'
        // A student passes filtering if
        // A) There are no filters set
        // B) Their status matches the singular filter
        // C) They are both active and pending and the filter is not archived
        const checkStatusFilter = filterStatuses.value.length === 0
            || filterStatuses.value.length && filterStatuses.value.includes(studentStatus)
            || studentStatus === 'Mixed' && !filterStatuses.value.includes('Archived')
        return checkPrepFilter && checkStatusFilter
    })
})

const pagedActiveTabStudents = computed(() => {
    const perPageVal = perPage.value
    return filteredActiveTabStudents.value
        .slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
})

const activeTabColumns = computed(() => {
    return activeTab.value === 'student' ? studentColumns : archivedStudentColumns.value
})

const getInstructorNames = (studentId: string) => {
    const studentGroups = groupModule.getters.searchCurrentOrgGroups({
        studentId,
        type: 'system',
    })

    const instructorFullNames = studentGroups.reduce((acc, group) => {
        if (!group.owner) {
            return acc
        }
        const instructor = currentOrgInstructors.value?.[group.owner.objectId]
        const userId = instructor?.user.objectId
        const instructorUser = userId && currentOrgInstructorUsers.value?.[userId]

        if (!instructorUser || !instructor || instructor.status === 'archived') {
            return acc
        }
        const instructorName = [ instructorUser.firstName, instructorUser.lastName?.charAt(0) ].join(' ')
        if (!acc.includes(instructorName)) {
            acc.push(instructorName)
        }
        return acc
    }, [] as string[])

    return instructorFullNames
}

onMounted(async () => {
    await Promise.all([
        studentModule.actions.fetchCurrentOrgStudents(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchCurrentOrgExams(),
        groupModule.actions.fetchCurrentOrgGroups(),
        examModule.actions.fetchExamMetadata(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        organizationModule.actions.fetchCurrentOrganization(),
        groupModule.actions.fetchGroupsActiveStudentIds(),
    ] as Promise<void>[])

    isLoading.value = false

    // Start processing students asynchronously
    await processStudentsInChunks()
})

watch(() => props.forceShowStudentsSidePanel, (val: boolean) => {
    if (val) {
        showAddStudentSidePanel.value = true
    }
})

watch(activeTab, () => {
    // Reset page
    currentPage.value = 0

    // Reset filters
    filterStatuses.value = []
    filterPrep.value = []
    checkboxStatuses.value = []
    checkboxPrep.value = []

    // Hide Menu
    showFilterDropdown.value = false

    // Reset checkbox selections
    studentIdSet.value = new Set()
})

watch(currentOrgLicenses, async () => {
    // Reprocess when licenses change (e.g., new students added)
    await processStudentsInChunks()
}, { deep: true })

watch(sortSettings, () => {
    // Reprocess when sort changes
    sortStudentsAndUpdateCache(mappedActiveStudentsCache.value)
}, { deep: true })

watch([ filteredActiveTabStudents, studentIds ], () => {
    if (studentIds.value.length > filteredActiveTabStudents.value.length) {
        // We are in a state where more students are selected than visible... adjust
        studentIdSet.value = new Set(filteredActiveTabStudents.value.map(ats => ats.id))
    }
})

watch(() => filteredActiveTabStudents.value.length, () => {
    const perPageVal = perPage.value
    if (filteredActiveTabStudents.value.length < perPageVal) {
        currentPage.value = 0
    }
})

const closeAddStudentsSidePanel = () => {
    showAddStudentSidePanel.value = false
    router.replace({ query: { ...route.query, addStudents: undefined } })
}

const removePrepFilters = (prep: string[]) => {
    prepFiltersToRemove.value = prep
}

const removeStatusFilters = (status: string[]) => {
    statusFiltersToRemove.value = status
}

const updatePrepFilter = ({ prep, trimmedPrep }: {
    prep: string[]
    trimmedPrep: string[]
}) => {
    filterPrep.value = trimmedPrep
    checkboxPrep.value = prep

    // Reset page
    currentPage.value = 0

    // Reset checkbox selections
    studentIdSet.value = new Set()
}

const selectAllStudents = () => {
    // Either all already selected or intermediate state (modelValue is null)
    if (studentsSelected.value === 'all' || studentsSelected.value === 'intermediate') {
        studentIdSet.value = new Set()
    } else {
        studentIdSet.value = new Set(filteredActiveTabStudents.value.map(ats => ats.id))
    }
}

const updateStatusesFilter = ({ statuses, trimmedStatuses }: {
    statuses: string[]
    trimmedStatuses: string[]
}) => {
    filterStatuses.value = trimmedStatuses
    checkboxStatuses.value = statuses

    // Reset page
    currentPage.value = 0

    // Reset checkbox selections
    studentIdSet.value = new Set()
}

const showPurchaseSeatsSidePanel = () => {
    showPurchaseSeatsModal.value = true

    if (currentOrganization.value) {
        // Fire amplitude event
        analyticsModule.actions.amplitudeTrack('Side Panel Opened', {
            sidePanelName: 'Purchase Seats',
            sidePanelPlacement: 'Settings Students Table',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
}

const processStudentsInChunks = async () => {
    // Capture start time for this processing run
    const currentStartTime = Date.now()
    latestProcessingStartTime.value = currentStartTime

    isProcessingStudents.value = true

    // Cache frequently accessed data outside the loop
    const isAdmin = currentOrgInstructor.value?.isAdmin
    const yourStudentIdSet = new Set(yourStudentIds.value) // O(1) lookups instead of O(n)
    const currentOrgStudentsVal = currentOrgStudents.value
    const currentOrgStudentUsersVal = currentOrgStudentUsers.value

    // Cache exam metadata lookups - avoid calling latestExamMetadataForGuid repeatedly
    // Build a cache from the sorted metadata per guid
    const guidToLatestMetadata: { [guid: string]: Study.Class.ExamMetadataJSON } = {}
    const guidExamMetadataLib = examModule.getters.getGuidExamMetadataLib()
    for (const guid in guidExamMetadataLib) {
        const metadataArray = guidExamMetadataLib[guid]
        if (metadataArray && metadataArray.length > 0) {
            const sortedExamMetadataForGuid = examModule.getters.getSortedExamMetadataForGuid(guid)[0]
            if (sortedExamMetadataForGuid) {
                guidToLatestMetadata[guid] = sortedExamMetadataForGuid
            }
        }
    }

    // Pre-compute instructor names cache
    const studentInstructorsCache = new Map<string, string>()

    const getInstructorNamesOptimized = (studentId: string): string => {
        if (studentInstructorsCache.has(studentId)) {
            const cached = studentInstructorsCache.get(studentId)
            return cached || '--'
        }
        const names = getInstructorNames(studentId)
        const result = names.length > 1 ? `${names.length} Instructors` : names[0] || '--'
        studentInstructorsCache.set(studentId, result)
        return result
    }

    // Group licenses by student in a single pass with filtering
    const groupedUserLicenses = currentOrgLicenses.value.reduce((acc, l) => {
        // Filter and group in one pass
        if (l.status !== 'pending' && l.status !== 'consumed' && l.status !== 'archived') {
            return acc
        }

        const studentId = l.orgStudent?.objectId
        if (!studentId) {
            return acc
        }

        if (!(studentId in acc)) {
            acc[studentId] = []
        }

        acc[studentId]?.push(l)

        return acc
    }, {} as { [key: string]: Study.Class.LicenseJSON[] })

    const allStudentIdsWithLicenses = Object.entries(groupedUserLicenses)

    // Filter entries once upfront based on permissions
    const yourStudentIdsWithLicenses: { studentId: string; licenses: Study.Class.LicenseJSON[] }[] = []
    for (const studentIdWithLicenses of allStudentIdsWithLicenses) {
        const studentId = studentIdWithLicenses[0]
        const licenses = studentIdWithLicenses[1]
        if (isAdmin || yourStudentIdSet.has(studentId)) {
            yourStudentIdsWithLicenses.push({ studentId, licenses })
        }
    }

    // Process in chunks to keep UI responsive
    const CHUNK_SIZE = 100 // Process 100 students at a time
    const processedMappedStudents: TMappedStudent[] = []

    for (let i = 0; i < yourStudentIdsWithLicenses.length; i += CHUNK_SIZE) {
        // Check if this processing run is still valid (hasn't been superseded by a newer run)
        if (currentStartTime !== latestProcessingStartTime.value) {
            isProcessingStudents.value = false
            return
        }

        const chunkedStudentIdsWithLicenses = yourStudentIdsWithLicenses.slice(i, i + CHUNK_SIZE)
        for (const studentIdWithLicenses of chunkedStudentIdsWithLicenses) {
            const studentId = studentIdWithLicenses.studentId
            const licenses = studentIdWithLicenses.licenses
            let student = licenses[0]?.userEmail
            const styles: { [key: string]: string } = {}

            const currentStudent = currentOrgStudentsVal[studentId]
            const studentUserId = currentStudent && 'user' in currentStudent
                && currentStudent.user?.objectId
            if (currentStudent?.displayFirstName || currentStudent?.displayLastName) {
                student = `${currentStudent?.displayFirstName} ${currentStudent?.displayLastName}`
            } else if (studentUserId) {
                const studentUser = currentOrgStudentUsersVal[studentUserId]
                student = `${studentUser?.firstName} ${studentUser?.lastName}`
            }

            // Should exit if the student is not readable for some reason
            if (!student) {
                continue
            }

            // Compute all status flags in one pass through licenses
            let isPending = false
            let isActive = false
            let isArchived = false
            const prepSet = new Set<string>()
            const studentExamGuids: string[] = []

            for (const l of licenses) {
                // Track status flags
                if (l.status === 'pending') {
                    isPending = true
                }
                if (l.status === 'consumed') {
                    isActive = true
                }
                if (l.status === 'archived') {
                    isArchived = true
                }

                // Build prep and exam guids in same pass
                const guid = l.examGuid
                if (guid) {
                    studentExamGuids.push(guid)
                    const e = guidToLatestMetadata[guid]
                    if (e) {
                        prepSet.add(e.nativeAppName)
                    }
                }
            }

            if (isPending && !isActive) {
                styles.color = BrandColors.ash || ''
            }

            const prep = Array.from(prepSet)
            const prepDisplayString = prep.length > 1
                ? `${prep.length} Exams`
                : prep[0]

            if (!prepDisplayString) {
                continue
            }

            processedMappedStudents.push({
                id: studentId,
                styles,
                studentNameOrEmail: student,
                prepDisplayString,
                prep,
                studentExamGuids,
                instructors: getInstructorNamesOptimized(studentId),
                startDate: licenses[0]?.invitedAt?.iso ? formatDate(licenses[0].invitedAt?.iso) : '',
                isPending,
                isActive,
                isArchived,
            })
        }

        // Update cache with partial results so UI can show progress
        mappedActiveStudentsCache.value = [ ...processedMappedStudents ]

        // Yield to browser to keep UI responsive
        await new Promise(resolve => setTimeout(resolve, 0))
    }

    // Sort the final results
    sortStudentsAndUpdateCache(processedMappedStudents)
    isProcessingStudents.value = false
}

const sortStudentsAndUpdateCache = (studentsToSort: TMappedStudent[]) => {
    const sortSettingsVal = sortSettings.value
    const sortedResults = [ ...studentsToSort ].sort((a, b) => {
        if (sortSettingsVal?.column) {
            if (sortSettingsVal.column.name === 'Student') {
                return sortSettingsVal.direction * a.studentNameOrEmail.localeCompare(b.studentNameOrEmail)
            }
            if (sortSettingsVal.column.name === 'Prep') {
                return sortSettingsVal.direction * a.prepDisplayString.localeCompare(b.prepDisplayString)
            }
            if (sortSettingsVal.column.name === 'Start Date') {
                const aStartDate = new Date(a.startDate).getTime()
                const bStartDate = new Date(b.startDate).getTime()
                return sortSettingsVal.direction * (bStartDate - aStartDate)
            }
        }
        return -1
    })

    mappedActiveStudentsCache.value = sortedResults
}

const resetFilters = () => {
    // Reset filter menu
    removePrepFilters(filterPrep.value)
    removeStatusFilters(filterStatuses.value)
    // Reset filters
    filterStatuses.value = []
    filterPrep.value = []
    checkboxStatuses.value = []
    checkboxPrep.value = []

    currentPage.value = 0

    // Reset checkbox selections
    studentIdSet.value = new Set()
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const rowClicked = (row: { id: string }) => {
    if (studentIdSet.value.has(row.id)) {
        studentIdSet.value = new Set([ ...studentIdSet.value ].filter(id => id !== row.id))
    } else {
        studentIdSet.value = new Set([ ...studentIdSet.value, row.id ])
    }
    return studentIds.value
}

const submitCancelInvites = async () => {
    const currentOrganizationVal = currentOrganization.value
    if (!currentOrganizationVal) {
        throw new Error('Unable to find current organization')
    }
    const examGuids = getStudentExamGuids('pending').filter(guid => guid !== undefined)
    const orgExamIds = getStudentOrgExamIds('pending')

    // Selected ids might be a mix of active and pending
    const pendingStudentIds = studentIds.value.filter(sid => {
        return selectedStudentsTypes.value.Pending?.find(sst => sst.id === sid)
    })

    const numPendingStudents = pendingStudentIds.length

    await studentModule.actions.cancelPendingSeatsBulk({
        orgStudentIds: pendingStudentIds,
        examGuids,
        orgExamIds,
        organizationId: currentOrganizationVal.objectId,
    })

    // Also need to retrieve non-system student groups and remove them
    showCancelInviteModal.value = false

    toastModule.actions.displayToast({
        title: `${numPendingStudents} invite(s) canceled.`,
    })

    showFilterDropdown.value = false
    resetFilters()
}

const getStudentLicenses = (status: 'consumed' | 'pending' | 'archived') => {
    return Object.values(currentOrgLicenses.value).filter(l => {
        return l.orgStudent?.objectId
            && studentIdSet.value.has(l.orgStudent?.objectId)
            && l.examGuid && l.status === status
    })
}

const getStudentExamGuids = (status: 'consumed' | 'pending') => {
    return getStudentLicenses(status).map(l => l.examGuid)
}

const getStudentOrgExamIds = (status: 'consumed' | 'pending') => {
    return getStudentExamGuids(status).reduce((acc, examGuid) => {
        const orgExams = examGuid && guidOrgExamLib.value[examGuid]
        if (orgExams) {
            acc.push(...orgExams.map(orgExam => orgExam.objectId))
        }
        return acc
    }, [] as string[])
}

const getSelectedStudentOrgExamIds = () => {
    return Object.values(currentOrgLicenses.value).reduce((acc, col) => {
        const isSelectedStudentExam = col.orgStudent?.objectId
            && studentIdSet.value.has(col.orgStudent?.objectId)
        if (isSelectedStudentExam && col.examGuid) {
            // Extract guid and convert to orgExamId
            const orgExamId = guidOrgExamLib.value[col.examGuid]?.[0]?.objectId
            if (orgExamId) {
                acc.push(orgExamId)
            }
        }
        return acc
    }, [] as string[])
}

const submitArchiveSeats = async () => {
    const licenses = getStudentLicenses('consumed')

    const currentOrganizationVal = currentOrganization.value

    // Capture the # here
    const numStudentsToArchive = selectedStudentsTypes.value.Active?.length
    const currentOrgInstructorVal = currentOrgInstructor.value
    if (!currentOrganizationVal) {
        throw new Error('submitArchiveSeats: No current organization found')
    }
    if (!licenses) {
        throw new Error('submitArchiveSeats: No licenses found')
    }
    if (!currentOrgInstructorVal) {
        throw new Error('submitArchiveSeats: No current instructor found')
    }

    await studentModule.actions.archiveSeats({
        licenseIds: licenses.map(l => l.objectId),
        orgInstructorId: currentOrgInstructorVal.objectId,
    })
    showArchiveStudentModal.value = false

    toastModule.actions.displayToast({
        title: `${numStudentsToArchive} student(s) archived.`,
    })

    showFilterDropdown.value = false
    resetFilters()
}

const submitUnarchiveSeats = async () => {
    const licenses = getStudentLicenses('archived')
    const currentOrganizationVal = currentOrganization.value

    if (!licenses) {
        throw new Error('submitArchiveSeat: No license found')
    }

    if (!currentOrganizationVal) {
        throw new Error('Unable to find current organization')
    }

    // Preserve this for the toast
    const numStudentsToUnarchive = studentIds.value.length

    await studentModule.actions.unarchiveSeats({
        licenseIds: licenses.map(l => l.objectId),
        organizationId: currentOrganizationVal.objectId,
    })

    toastModule.actions.displayToast({
        title: `${numStudentsToUnarchive} student(s) unarchived.`,
    })

    showFilterDropdown.value = false
    resetFilters()
}

const editStudent = (studentId: string) => {
    editStudentId.value = studentId
    showEditStudentSidePanel.value = true
}

const emptyStateTitle = computed(() => {
    return 'There\'s nobody here yet'
})

const emptyStateMessage = computed(() => {
    if (activeTab.value === 'archived') {
        return 'Archived students will appear here.'
    }

    return !currentOrgLicenses.value.length && showPurchaseSeatsButton.value
        ? 'Purchase seats to add your students and give them access to our test prep.'
        : 'Get started by adding some students.'
})

const updateSortSettings = (sortSettingsVal: ITableSortSettings) => {
    const current = sortSettings.value
    const columnChanged = current.column?.propName !== sortSettingsVal.column?.propName
    const directionChanged = current.direction !== sortSettingsVal.direction
    if (!columnChanged && !directionChanged) {
        return
    }
    sortSettings.value = sortSettingsVal
    if (isProcessingStudents.value) {
        return
    }
    sortStudentsAndUpdateCache(mappedActiveStudentsCache.value)
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const hideCellTooltip = () => {
    cellHovered.value = null
}

const showCellTooltip = (propName: string, id: string) => {
    cellHovered.value = {
        propName,
        id,
    }
}
</script>

<style lang="scss" scoped>


.students {
    &__no-available-seats-banner {
        margin-bottom: 20px;
    }

    &__tabs {
        border-bottom: 2px solid $gray-divider;
        display: flex;
        font-size: 16px;
        line-height: 22px;
        font-weight: 500;

        .students__tab {
            position: relative;
            padding: 6px 8px;
            cursor: pointer;
            user-select: none;
            margin-right: 9px;

            &.active {
                font-weight: 600;

                &::after {
                    content: '';
                    width: 100%;
                    height: 2px;
                    bottom: -2px;
                    left: 0;
                    position: absolute;
                    background: $brand-blue;
                }
            }
        }
    }

    &__processing-indicator {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: $slate-03;
        margin-left: 16px;
    }

    &__instructors {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__edit {
        cursor: pointer;
        height: 24px;
        color: $brand-blue;
    }

    &__edit-tooltip {
        position: absolute;
        transform: translate(0, -100%);
        left: 12px;
        top: -3px;
    }

    &__status {
        text-transform: capitalize;
    }

    &__status-tooltip {
        position: absolute;
        transform: translate(20px, -100%);
        top: 0;
    }

    &__table-head {
        display: flex;
        margin: 11px 11px 20px;
        align-items: center;
    }

    &__table-search {
        width: 262px;
    }

    &__filter {
        margin-left: 10px;
        display: table;

        // Unfortunately, it seems like these need to be separated for css to get it
        &:hover div {
            cursor: pointer;
            color: $brand-black;
        }

        &:hover .students__filter-btn {
            cursor: pointer;
            color: $brand-black;
        }
    }

    &__filter-btn {
        display: table-cell;
    }

    &__filter-text {
        font-size: 15px;
        line-height: 24px;
        font-weight: 600;
        min-height: 24px;
        display: table-cell;
        vertical-align: middle;
        color: $brand-blue;
    }

    &__filter-dropdown {
        position: absolute;
        z-index: 2;
        width: 240px;
    }

    &__bulk-actions {
        display: flex;
        margin-bottom: 11px;
    }

    &__bulk-actions-checkbox {
        margin-left: 12px;
    }

    &__bulk-actions-num-checked {
        font-size: 12px;
        line-height: 16px;
        margin: 2px 30px;
        margin-left: 20px;
    }

    &__checkbox-name {
        display: flex;
        align-items: center;
    }

    &__checkbox {
        margin-right: 10px;
    }

    &__table-pagination {
        margin-left: 150px;
    }

    &__filter-chips {
        min-height: 30px;
        margin: 0 0 13px 3px;
    }

    &__filter-chips-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;
    }

    &__name {
        width: calc(100% - 28px);
    }

    &__add-to-group-btn,
    &__resend-invite-btn,
    &__cancel-invite-btn,
    &__add-student-btn {
        padding-right: 36px;
    }

    &__resend-invite,
    &__archive-student,
    &__cancel-invite,
    &__add-to-group {
        position: relative;
        display: inline-block;
    }

    &__resend-invite-tooltip,
    &__cancel-invite-tooltip,
    &__archive-student-tooltip,
    &__add-to-group-tooltip {
        position: absolute;
        left: 35%;
        top: -150%;
    }

    &__statuses {
        display: inline-flex;
    }

    &__status-active {
        margin-right: 6px;
    }

    &__cancel-invite-head {
        margin: 60px auto 25px;
        font-size: 18px;
        color: $brand-black;
        font-weight: 600;
    }

    &__cancel-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 374px;
        box-sizing: border-box;
    }

    &__nevermind-btn {
        margin-right: 18px;
        color: $slate;
    }

    &__cancel-buttons {
        display: flex;
        margin: 32px 0 24px;
    }

    &__cancel-text {
        font-size: 15px;
        color: $ash;
        width: 294px;
        text-align: center;
        margin: 20px 0 0;
    }

    &__empty-state {
        margin: 0 auto;
        padding: 40px 0 0;
        min-width: 300px;
    }

    &__add-student-empty-btn {
        display: table;
        margin: 22px auto;
    }
}
</style>
